'use client';

import { ArrowRight, Check, ChevronDown } from 'lucide-react';

import { useEffect, useMemo, useState } from 'react';

import Link from 'next/link';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { DEFAULT_FORM_TITLE } from '@/features/form-editing/components/constants/form-edit-constants';
import FormWidgetsDisplay from '@/features/form-filling/components/common/form-widgets-display';

import { Tooltip } from '@/shared/components/tooltip';
import { COPY } from '@/shared/constants/copies';
import type { TFormWidget } from '@/shared/services/forms/forms.types';
import type { PublishedForm } from '@/shared/services/page/careers';
import { isRunningInIframe } from '@/shared/utils/window';

import { cn } from '@/utils/styles';

type JobsListProps = {
	forms: PublishedForm[];
	isEditMode?: boolean;

	pinnedForms?: string[];
	onTogglePin?: (identifier: string) => void;
	maxPinnedForms?: number;
};

/**
 * Compute which widgets to pass to FormWidgetsDisplay for a job.
 * - If the API returns includedWidgets (even an empty array), always trust it.
 *   FormWidgetsDisplay will suppress any widget whose data doesn't meet its
 *   visibility threshold (e.g. response rate < 80%, applicant count < 25, etc.)
 *   — exactly the same logic used in filling-section.tsx.
 * - If includedWidgets is absent (older API response), fall back to inferring
 *   from available fields.
 */
function getDisplayWidgets(form: PublishedForm): TFormWidget[] {
	if (form.includedWidgets !== undefined) {
		return form.includedWidgets;
	}
	// Fallback: infer from whichever fields the API did return
	const widgets: TFormWidget[] = [];
	if (form.locations && form.locations.length > 0) widgets.push('LOCATION');
	if (form.closingDate) widgets.push('CLOSING_DATE');
	if (form.counts?.totalCount) widgets.push('NUMBER_OF_APPLICANTS');
	if (form.counts?.respondedCount && form.counts?.totalCount) widgets.push('RESPONSE_PERCENTAGE');
	return widgets;
}

export function JobsList({
	forms,
	isEditMode,
	pinnedForms,
	onTogglePin,
	maxPinnedForms = 2,
}: JobsListProps) {
	const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
	const [filterOpen, setFilterOpen] = useState(false);
	const [isInIframe, setIsInIframe] = useState(false);

	useEffect(() => {
		setIsInIframe(isRunningInIframe());
	}, []);

	// Reset location filter when entering edit mode
	useEffect(() => {
		if (isEditMode) {
			setSelectedLocation(null);
			setFilterOpen(false);
		}
	}, [isEditMode]);

	const pinnedSet = useMemo(() => new Set(pinnedForms ?? []), [pinnedForms]);

	// Enrich forms with derived fields (isNew badge, display widgets)
	const enrichedForms = useMemo(
		() =>
			forms.map((form) => ({
				...form,
				status: form.status ?? 'PUBLISHED',
				displayWidgets: getDisplayWidgets(form),
			})),
		[forms]
	);

	// Collect all unique locations across forms
	const allLocations = useMemo(() => {
		const locs = new Set<string>();
		enrichedForms.forEach((form) => {
			form.locations?.forEach((loc) => locs.add(loc));
		});
		return Array.from(locs).sort();
	}, [enrichedForms]);

	// Filter forms by selected location
	const filteredForms = useMemo(() => {
		if (!selectedLocation) return enrichedForms;
		return enrichedForms.filter((form) => form.locations?.includes(selectedLocation));
	}, [enrichedForms, selectedLocation]);

	return (
		<div className="flex flex-col gap-4 sm:gap-5">
			{/* Open roles + location filter */}
			<div className="flex items-center justify-between gap-4">
				<p className="font-sf-pro-display text-[13px] font-semibold uppercase tracking-[0.04em] text-[#475569]">
					{forms.length} Open Role{forms.length !== 1 ? 's' : ''}
				</p>
				{allLocations.length > 0 && (
					<Popover
						open={filterOpen}
						onOpenChange={setFilterOpen}
					>
						<PopoverTrigger asChild>
							<button
								type="button"
								className="flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-secondary transition-colors hover:text-primary"
							>
								{selectedLocation ?? 'All Locations'}
								<ChevronDown
									className={cn('h-4 w-4 transition-transform', filterOpen ? 'rotate-180' : '')}
								/>
							</button>
						</PopoverTrigger>
						<PopoverContent
							className="w-[200px] p-1"
							align="end"
						>
							<div className="max-h-64 overflow-y-auto">
								<button
									type="button"
									className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
									onClick={() => {
										setSelectedLocation(null);
										setFilterOpen(false);
									}}
								>
									<Check
										className={cn(
											'mr-2 h-3.5 w-3.5',
											!selectedLocation ? 'opacity-100' : 'opacity-0'
										)}
									/>
									All Locations
								</button>
								{allLocations.map((loc) => (
									<button
										key={loc}
										type="button"
										className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
										onClick={() => {
											setSelectedLocation(loc);
											setFilterOpen(false);
										}}
									>
										<Check
											className={cn(
												'mr-2 h-3.5 w-3.5',
												selectedLocation === loc ? 'opacity-100' : 'opacity-0'
											)}
										/>
										{loc}
									</button>
								))}
							</div>
						</PopoverContent>
					</Popover>
				)}
			</div>

			{/* Jobs list */}
			<div className="overflow-visible rounded-lg border border-[#E2E8F0] bg-card">
				{filteredForms.map((job, index) => {
					const isFirst = index === 0;
					const isLast = index === filteredForms.length - 1;
					const hasWidgets = job.displayWidgets.length > 0;
					const isPinned = pinnedSet.has(job.identifier);

					return (
						<div
							key={job.identifier}
							className={cn(
								'group relative flex items-center gap-4 px-4 py-4 transition-all sm:px-5 sm:py-5',
								!isLast && 'border-b border-[#E2E8F0]',
								isFirst && 'rounded-t-lg',
								isLast && 'rounded-b-lg',
								isEditMode ? 'cursor-default' : 'hover:bg-[#FAFAFA]'
							)}
						>
							{/* Overlay link for view mode navigation */}
							{!isEditMode && (
								<Link
									href={`/${job.identifier}`}
									className="absolute inset-0 z-0"
									aria-label={job.title || DEFAULT_FORM_TITLE}
									target={isInIframe ? '_blank' : '_self'}
									rel={isInIframe ? 'noopener noreferrer' : undefined}
								/>
							)}

							{/* Min height = title line + gap + widget line, so rows match with or without widgets. */}
							<div className="flex min-h-[49px] w-full min-w-0 flex-col justify-center gap-1">
								<h2 className="truncate font-sf-pro-display text-base font-medium tracking-normal text-[#0F172A] sm:text-[17px]">
									{job.title || DEFAULT_FORM_TITLE}
								</h2>

								{hasWidgets && (
									<FormWidgetsDisplay
										includedWidgets={job.displayWidgets}
										closingDate={job.closingDate}
										totalCount={job.counts?.totalCount}
										respondedCount={job.counts?.respondedCount}
										formStatus={job.status}
										templateColor="blue"
										locations={job.locations}
										wrapText
										variant="minimal"
										className="my-0 flex-wrap gap-x-4 gap-y-1 md:my-0"
										formWidgetClassName="relative z-10 font-sf-pro-display px-0 py-0 text-sm font-normal leading-normal text-[#475569]"
									/>
								)}
							</div>

							{/* Right action area */}
							{isEditMode ? (
								<Tooltip
									text={
										!isPinned && pinnedSet.size >= maxPinnedForms
											? COPY.careers.maxPinned(maxPinnedForms)
											: undefined
									}
								>
									<button
										type="button"
										className={cn(
											'z-10 flex-shrink-0 rounded-md p-1.5 transition-colors',
											isPinned
												? 'text-muted-foreground hover:bg-muted'
												: pinnedSet.size >= maxPinnedForms
													? 'cursor-not-allowed text-muted-foreground/40'
													: 'text-muted-foreground hover:bg-muted hover:text-foreground'
										)}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											onTogglePin?.(job.identifier);
										}}
										aria-label={isPinned ? 'Unpin form' : 'Pin form'}
									>
										<PinIcon className={cn('size-4', isPinned && 'fill-muted-foreground')} />
									</button>
								</Tooltip>
							) : (
								<ArrowRight className="h-4 w-4 flex-shrink-0 text-primary opacity-0 transition-all sm:group-hover:opacity-100" />
							)}
						</div>
					);
				})}

				{filteredForms.length === 0 && (
					<div className="p-8 text-center text-sm text-muted-foreground">
						No roles found for this location.
					</div>
				)}
			</div>
		</div>
	);
}

const PinIcon = (props: React.SVGProps<SVGSVGElement>) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		width={24}
		height={24}
		color="currentColor"
		fill="none"
		{...props}
	>
		<path
			d="M3 21L8 16"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M13.2585 18.8714C9.51516 18.0215 5.97844 14.4848 5.12853 10.7415C4.99399 10.1489 4.92672 9.85266 5.12161 9.37197C5.3165 8.89129 5.55457 8.74255 6.03071 8.44509C7.10705 7.77265 8.27254 7.55888 9.48209 7.66586C11.1793 7.81598 12.0279 7.89104 12.4512 7.67048C12.8746 7.44991 13.1622 6.93417 13.7376 5.90269L14.4664 4.59604C14.9465 3.73528 15.1866 3.3049 15.7513 3.10202C16.316 2.89913 16.6558 3.02199 17.3355 3.26771C18.9249 3.84236 20.1576 5.07505 20.7323 6.66449C20.978 7.34417 21.1009 7.68401 20.898 8.2487C20.6951 8.8134 20.2647 9.05346 19.4039 9.53358L18.0672 10.2792C17.0376 10.8534 16.5229 11.1406 16.3024 11.568C16.0819 11.9955 16.162 12.8256 16.3221 14.4859C16.4399 15.7068 16.2369 16.88 15.5555 17.9697C15.2577 18.4458 15.1088 18.6839 14.6283 18.8786C14.1477 19.0733 13.8513 19.006 13.2585 18.8714Z"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);
